package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.Task
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.newId
import org.teddapp.project.db.notifyLeafIfChanged
import org.teddapp.project.db.nowMillis

class TaskRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Task>> =
        db.taskQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeById(id: String): Flow<Task?> =
        db.taskQueries.selectById(id).asFlow().mapToOneOrNull(Dispatchers.Default)

    fun observeByMilestone(milestoneId: String): Flow<List<Task>> =
        db.taskQueries.selectByMilestone(milestoneId).asFlow().mapToList(Dispatchers.Default)

    fun observeByProject(projectId: String): Flow<List<Task>> =
        db.taskQueries.selectByProject(projectId).asFlow().mapToList(Dispatchers.Default)

    fun observeByProjectStatus(statusId: String): Flow<List<Task>> =
        db.taskQueries.selectByProjectStatus(statusId).asFlow().mapToList(Dispatchers.Default)

    fun observeStandalone(): Flow<List<Task>> =
        db.taskQueries.selectStandalone().asFlow().mapToList(Dispatchers.Default)

    fun observeDueBetween(from: String, to: String): Flow<List<Task>> =
        db.taskQueries.selectDueBetween(from, to).asFlow().mapToList(Dispatchers.Default)

    fun observePending(): Flow<List<Task>> =
        db.taskQueries.selectPending().asFlow().mapToList(Dispatchers.Default)

    suspend fun create(
        parentKind: ParentKind,
        title: String,
        parentId: String? = null,
        statusId: String? = null,
        description: String? = null,
        status: String = "pending",
        priority: String? = null,
        effort: Long? = null,
        dueDate: String? = null,
        estimatedMinutes: Long? = null,
        realMinutes: Long? = null,
        recurrence: String? = null,
        pomodoroPoolDate: String? = null,
        orderIndex: Long = 0,
        id: String = newId("t"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.taskQueries.insert(
            id,
            parentKind.raw,
            parentId,
            statusId,
            title,
            description,
            status,
            priority,
            effort,
            dueDate,
            estimatedMinutes,
            realMinutes,
            recurrence,
            pomodoroPoolDate,
            orderIndex,
            null, // completed_at
            now,
            now,
        )
        id
    }

    suspend fun update(
        id: String,
        parentKind: ParentKind,
        parentId: String?,
        statusId: String?,
        title: String,
        description: String?,
        status: String,
        priority: String?,
        effort: Long?,
        dueDate: String?,
        estimatedMinutes: Long?,
        realMinutes: Long?,
        recurrence: String?,
        pomodoroPoolDate: String?,
        orderIndex: Long,
        completedAt: Long?,
    ) = withContext(Dispatchers.Default) {
        db.taskQueries.update(
            parentKind.raw,
            parentId,
            statusId,
            title,
            description,
            status,
            priority,
            effort,
            dueDate,
            estimatedMinutes,
            realMinutes,
            recurrence,
            pomodoroPoolDate,
            orderIndex,
            completedAt,
            nowMillis(),
            id,
        )
    }

    /** Incrémente le temps réel passé (typiquement appelé par le module Pomodoro). */
    suspend fun addRealMinutes(id: String, minutes: Long) = withContext(Dispatchers.Default) {
        db.taskQueries.addRealMinutes(minutes, nowMillis(), id)
    }

    /** Ajoute / retire la tâche du pool Pomodoro pour une date donnée (null = retirer). */
    suspend fun setPomodoroPool(id: String, date: String?) = withContext(Dispatchers.Default) {
        db.taskQueries.setPomodoroPool(date, nowMillis(), id)
    }

    suspend fun markDone(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.taskQueries.markDone(now, now, id)
        org.teddapp.project.db.RecurrenceScheduler.onCompleted(db, id)
    }

    /** Change la récurrence d'une micro-tâche sans toucher au reste. */
    suspend fun setRecurrence(id: String, recurrence: String?) = withContext(Dispatchers.Default) {
        db.taskQueries.updateRecurrence(recurrence, nowMillis(), id)
    }

    suspend fun markPending(id: String) = withContext(Dispatchers.Default) {
        db.taskQueries.markPending(nowMillis(), id)
    }

    suspend fun markSkipped(id: String) = withContext(Dispatchers.Default) {
        db.taskQueries.markSkipped(nowMillis(), id)
    }

    suspend fun toggleDone(task: Task) {
        if (task.status == "done") markPending(task.id) else markDone(task.id)
    }

    suspend fun moveToStatus(id: String, statusId: String?, orderIndex: Long) = withContext(Dispatchers.Default) {
        val before = db.taskQueries.selectByIdAnyState(id).executeAsOneOrNull()
        db.taskQueries.updateProjectStatus(statusId, orderIndex, nowMillis(), id)
        // Une action de parcours rangée dans une colonne terminale est faite : le noyau doit le savoir.
        if (before != null) notifyLeafIfChanged(db, before)
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.taskQueries.softDelete(now, now, id)
    }

    /**
     * Réordonne les tâches d'un jalon (spec §7.5). Intra-jalon uniquement.
     */
    suspend fun reorderInMilestone(milestoneId: String, fromIndex: Int, toIndex: Int) =
        withContext(Dispatchers.Default) {
            if (fromIndex == toIndex) return@withContext
            val list = db.taskQueries.selectByMilestone(milestoneId).executeAsList().toMutableList()
            if (fromIndex !in list.indices) return@withContext
            val clampedTo = toIndex.coerceIn(0, list.lastIndex)
            val item = list.removeAt(fromIndex)
            list.add(clampedTo, item)
            val now = nowMillis()
            list.forEachIndexed { i, t ->
                if (t.order_index != i.toLong()) {
                    db.taskQueries.updateOrder(i.toLong(), now, t.id)
                }
            }
        }
}